'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import I18n from 'I18n';
import unescapedText from 'I18n/utils/unescapedText';
import AbstractTextElement from 'UIComponents/elements/AbstractTextElement';
import * as AssetBusinessType from 'marketplace-ui-assets-core/constants/AssetBusinessType';
import * as AssetPageType from 'marketplace-ui-assets-core/constants/AssetPageType';
import * as AssetPageFeature from 'marketplace-ui-assets-core/constants/AssetPageFeature';
import * as EmbedFrame from 'marketplace-embed/constants/EmbedFrame';
import * as EmbedGroup from 'marketplace-embed/constants/EmbedGroup';
import * as OfferingType from 'marketplace-ui-common/offerings/constants/OfferingType';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import * as ThemeQueryField from 'marketplace-ui-assets-core/constants/ThemeQueryField';
import * as PostMessageType from 'marketplace-embed/constants/PostMessageType';
import { useAssetSearchBarQuery, VIEW_ALL_THEMES, VIEW_ALL_MODULES } from 'marketplace-ui-assets-core/search/hooks/useAssetSearchBarQuery';
import { TYPEAHEAD_SEARCH_MIN_CHARS } from 'marketplace-ui-common/constants/search';
import { buildGetCachedSearchValues, buildSetCachedSearchValue } from 'ecosystem-marketplace-core/utils/cachedSearch';
import { debounce } from 'marketplace-ui-common/utils/debounce';
import { getAssetBusinessTypeI18nKey, getAssetPageTypeI18nKey, getAssetPageFeatureI18nKey } from 'ecosystem-marketplace-core/utils/i18n';
import InputHighlighter from 'ecosystem-marketplace-core/components/storefront/InputHighlighter';
import MediaQuery from 'marketplace-ui-common/layout/components/MediaQuery';
import SearchBar from 'marketplace-ui-common/search/components/SearchBar';
import * as ModuleQueryField from '../../../modules/constants/ModuleQueryField';
import { useEmbedContext } from '../../../hooks/useEmbedContext';
import { getIsAdminPreview, getIsPublicApp } from '../../../selectors/app';
import { getIsUngatedForUnifiedSearch } from '../../../selectors/auth';
import { getDetailsRoute } from '../../../utils/urls';
import { useOfferingType } from '../../../context/OfferingTypeContext';
import { getComputedQuery, getLocationFromQuery } from '../../../common/utils/assetQuery';
import MobileSearchBar from './MobileSearchBar';
import ModuleSearchResponse from '../../../modules/records/ModuleSearchResponse';
import StorefrontUsageTrackerContext from '../../../context/StorefrontUsageTrackerContext';
import ThemeSearchResponse from '../../../records/ThemeSearchResponse';
const CACHED_SEARCH_VALUES_KEY = 'marketplace-assets:searches';
const getCachedSearchValues = buildGetCachedSearchValues(CACHED_SEARCH_VALUES_KEY);
const setCachedSearchValue = buildSetCachedSearchValue(CACHED_SEARCH_VALUES_KEY);

const getFullFilterGroup = (filterTypes, getTranslationKey, filterKey, message) => {
  const options = filterTypes.map(filterType => {
    const translatedText = I18n.text(getTranslationKey(filterType));
    return {
      dropdownText: /*#__PURE__*/_jsx(InputHighlighter, {
        text: translatedText
      }),
      filterKey,
      text: translatedText,
      value: filterType
    };
  });
  return {
    text: I18n.text(message),
    options
  };
};

const getDisplayedFilterGroup = (renderedOptions, input) => {
  const filteredOptions = renderedOptions.options.filter(option => option.text.toLowerCase().includes(input));
  return Object.assign({}, renderedOptions, {
    options: filteredOptions
  });
};

const StorefrontSearchBar = ({
  isSticky
}) => {
  const offeringType = useOfferingType();
  const [categorySelectValue, setCategorySelectValue] = useState(offeringType);
  const offeringTypeIsModule = categorySelectValue === OfferingType.MODULE;
  const assetQueryField = offeringTypeIsModule ? ModuleQueryField : ThemeQueryField;
  const emptySearchResponse = offeringTypeIsModule ? ModuleSearchResponse : ThemeSearchResponse;
  const {
    isEmbedded,
    name: embedFrameName,
    sendMessage,
    sendBroadcastMessage
  } = useEmbedContext();
  const history = useHistory();
  const location = useLocation();
  const cachedSearchValues = getCachedSearchValues();
  const {
    trackInteraction
  } = useContext(StorefrontUsageTrackerContext);
  const isAdminPreview = useSelector(getIsAdminPreview);
  const isPublicApp = useSelector(getIsPublicApp);
  const isUngatedForUnifiedSearch = useSelector(getIsUngatedForUnifiedSearch);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const getSearchPlaceholderKey = useCallback(() => {
    return unescapedText(`marketplace-assets-ui.storefront.header.search.placeholders.${categorySelectValue}`);
  }, [categorySelectValue]);
  const assetQueryFieldOrder = useMemo(() => {
    return [assetQueryField.BUSINESS_TYPES, assetQueryField.PAGE_TYPES, assetQueryField.PAGE_FEATURES];
  }, [assetQueryField]);
  const businessTypes = Object.values(AssetBusinessType);
  const filterGroups = useMemo(() => {
    if (!offeringTypeIsModule) {
      return {
        [assetQueryField.BUSINESS_TYPES]: getFullFilterGroup(businessTypes, getAssetBusinessTypeI18nKey, assetQueryField.BUSINESS_TYPES, 'marketplace-assets-ui.assets.filters.businessType.title'),
        [assetQueryField.PAGE_TYPES]: getFullFilterGroup(Object.values(AssetPageType), getAssetPageTypeI18nKey, assetQueryField.PAGE_TYPES, 'marketplace-assets-ui.assets.filters.pageTypes.title'),
        [assetQueryField.PAGE_FEATURES]: getFullFilterGroup(Object.values(AssetPageFeature), getAssetPageFeatureI18nKey, assetQueryField.PAGE_FEATURES, 'marketplace-assets-ui.assets.filters.pageFeatures.title')
      };
    }

    return null;
  }, [assetQueryField, businessTypes, offeringTypeIsModule]);
  const computedQuery = useMemo(() => getComputedQuery(categorySelectValue, {
    pageNumber: 0
  }, location), [categorySelectValue, location]);
  const searchTerm = useMemo(() => {
    return computedQuery.get(assetQueryField.SEARCH) || '';
  }, [assetQueryField, computedQuery]);
  const searchInputSize = useMemo(() => {
    if (!isSticky) {
      return 'xl';
    }

    return 'default';
  }, [isSticky]);
  const recentSearchOptions = useMemo(() => {
    if (!cachedSearchValues.length) {
      return [];
    }

    return [{
      text: I18n.text(`marketplace-assets-ui.storefront.header.search.recent`),
      options: cachedSearchValues.map(value => ({
        text: value,
        value,
        cachedSearchValue: true
      }))
    }];
  }, [cachedSearchValues]);
  const handleAssetQueryChange = useCallback(assetQuery => {
    const newLocation = getLocationFromQuery(assetQuery, location, false, isUngatedForUnifiedSearch);
    history.push(newLocation);
  }, [history, isUngatedForUnifiedSearch, location]);
  const formatDropdownOptions = useCallback((recordData, input) => {
    const formattedOptions = [];

    if (recordData.assetOptions) {
      formattedOptions.push(...recordData.assetOptions);
    }

    if (!offeringTypeIsModule) {
      assetQueryFieldOrder.forEach(filterGroupKey => formattedOptions.push(getDisplayedFilterGroup(filterGroups[filterGroupKey], input)));
    }

    return formattedOptions;
  }, [assetQueryFieldOrder, filterGroups, offeringTypeIsModule]);
  const fetcher = useAssetSearchBarQuery({
    appContext: isPublicApp ? 'public' : 'app',
    isUngatedForUnifiedSearch,
    grouped: true,
    offeringType
  });
  const getCategorySelectOptions = useCallback(() => {
    return [{
      text: unescapedText('marketplace-assets-ui.storefront.header.search.categories.theme'),
      value: OfferingType.THEME,
      dropdownText: /*#__PURE__*/_jsx(AbstractTextElement, {
        weight: "demi",
        children: unescapedText('marketplace-assets-ui.storefront.header.search.categories.theme')
      })
    }, {
      text: unescapedText('marketplace-assets-ui.storefront.header.search.categories.module'),
      value: OfferingType.MODULE,
      dropdownText: /*#__PURE__*/_jsx(AbstractTextElement, {
        weight: "demi",
        children: unescapedText('marketplace-assets-ui.storefront.header.search.categories.module')
      })
    }];
  }, []);
  const handleUpdateSearchFilters = useCallback(value => {
    setCategorySelectValue(value);

    if (open && !searchInputValue) {
      setOpen(false);
    }
  }, [open, searchInputValue]);
  const handleCategorySelectChange = useCallback(({
    target: {
      value
    }
  }) => {
    handleUpdateSearchFilters(value);
    trackInteraction('select search category', {
      value
    }, 'storefront-select-search-category');
  }, [handleUpdateSearchFilters, trackInteraction]);
  const categorySelectProps = useMemo(() => {
    return {
      options: getCategorySelectOptions(),
      onChange: handleCategorySelectChange,
      value: categorySelectValue
    };
  }, [categorySelectValue, getCategorySelectOptions, handleCategorySelectChange]);
  const loadOptions = useCallback((input, callback) => {
    if (input.trim().length < TYPEAHEAD_SEARCH_MIN_CHARS) {
      callback(null, {
        options: recentSearchOptions
      });
      return;
    }

    const formatOptions = (resp, searchInput) => formatDropdownOptions({
      assetOptions: resp
    }, searchInput);

    fetcher(input).then(resp => {
      const options = formatOptions(resp, input);
      callback(null, {
        options
      });
    }).catch(() => {
      const options = formatOptions(emptySearchResponse, input);
      callback(null, {
        options
      });
    });
  }, [emptySearchResponse, recentSearchOptions, formatDropdownOptions, fetcher]);
  const debouncedLoadOptions = useMemo(() => debounce(loadOptions), [loadOptions]);
  const onSearchSubmit = useCallback(value => {
    const valuesFromCache = getCachedSearchValues();
    setCachedSearchValue(value, valuesFromCache);
    handleAssetQueryChange(computedQuery.set(assetQueryField.SEARCH, value));
    trackInteraction('submit search', {
      search: value,
      searchLength: value.length
    });
  }, [assetQueryField, computedQuery, handleAssetQueryChange, trackInteraction]);
  const trackSelectedOptionInteraction = useCallback((itemType, value) => {
    trackInteraction('select search autofill item', {
      itemType,
      value
    });
  }, [trackInteraction]);
  const onSelectedOptionChange = useCallback(option => {
    const {
      value,
      filterKey,
      cachedSearchValue,
      slug
    } = option;

    if (value === VIEW_ALL_THEMES || value === VIEW_ALL_MODULES) {
      onSearchSubmit(searchInputValue);
      trackSelectedOptionInteraction('see all results', searchInputValue);
      return;
    }

    if (filterKey) {
      handleAssetQueryChange(computedQuery.set(filterKey, value));
      trackSelectedOptionInteraction(filterKey, value);
      return;
    }

    if (cachedSearchValue) {
      onSearchSubmit(value);
      trackSelectedOptionInteraction('recent search', value);
      return;
    }

    if (isEmbedded && embedFrameName === EmbedFrame.CONTENT_CREATOR_STOREFRONT) {
      const messageArgs = [PostMessageType.NAVIGATE_TO_THEME_DETAILS, {
        slug
      }];
      sendBroadcastMessage(...messageArgs, EmbedGroup.CONTENT_CREATOR);
      sendMessage(...messageArgs);
      return;
    }

    history.push(getDetailsRoute(offeringType, slug));
    trackSelectedOptionInteraction('offering', slug);
  }, [computedQuery, embedFrameName, handleAssetQueryChange, history, isEmbedded, offeringType, onSearchSubmit, searchInputValue, sendBroadcastMessage, sendMessage, trackSelectedOptionInteraction]);
  const onClearSearch = useCallback(() => {
    handleAssetQueryChange(computedQuery.set('search', null));
  }, [computedQuery, handleAssetQueryChange]);
  return /*#__PURE__*/_jsx(MediaQuery, {
    alternate: /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(MobileSearchBar, {
        loadOptions: debouncedLoadOptions,
        onClearSearch: onClearSearch,
        onSearchSubmit: onSearchSubmit,
        onSelectedOptionChange: onSelectedOptionChange,
        placeholder: getSearchPlaceholderKey(),
        searchTerm: searchTerm
      })
    }),
    minScreenSize: ScreenSizes.SM,
    children: /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(SearchBar, {
        categorySelectProps: !isEmbedded && !isUngatedForUnifiedSearch ? categorySelectProps : undefined,
        disabled: isAdminPreview,
        loadOptions: debouncedLoadOptions,
        onSearchSubmit: onSearchSubmit,
        onSelectedOptionChange: event => onSelectedOptionChange(event.target.value),
        onInputValueChange: event => setSearchInputValue(event.target.value),
        inputSize: searchInputSize,
        inputValue: searchTerm,
        onClear: onClearSearch,
        placeholder: getSearchPlaceholderKey()
      })
    })
  });
};

export default StorefrontSearchBar;